import React, { useState, useEffect } from 'react';
import { formatStoredBookingDate } from '../utils/bookingDates';
import { 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  User,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { GOLD, GOLD_RGBA, CARD_BG } from '../constants/booking';
import { releaseHold } from '../services/adminService';

const formatPackageName = (name) => {
  if (!name) return 'Mobile Golf Simulator';
  const lower = String(name).toLowerCase();
  if (lower.includes('driver') || lower.includes("den")) return 'Mobile Golf Simulator';
  return name;
};

const BookingsTable = ({ onEditBooking }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [releasingId, setReleasingId] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const customerInfo = data.customerInfo || {};
        
        bookingsData.push({
          id: doc.id,
          bookingId: data.bookingId,
          customerName: customerInfo.fullName || data.fullName || 'Unknown',
          email: customerInfo.email || data.email || 'No email',
          phone: customerInfo.phoneNumber || data.phoneNumber || 'No phone',
          address: customerInfo.billingAddress || 'No address',
          date: data.date,
          time: data.time,
          duration: data.totalHours || (data.baseHours + data.extraHours) || 2,
          package: formatPackageName(data.packageName || data.package?.name),
          status: data.status || 'pending',
          kind: data.kind || (String(data.status).toUpperCase() === 'HOLD' ? 'hold' : 'booking'),
          total: data.total || 0,
          deposit: data.deposit || 0,
          addOns: data.addOnsNames || data.selectedAddOns || [],
          createdAt: data.createdAt?.toDate?.() || new Date(),
          startAt: data.startAt?.toDate?.() || null,
          endAt: data.endAt?.toDate?.() || null,
          userId: data.userId
        });
      });
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.bookingId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all'
      || (booking.status || '').toLowerCase() === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'createdAt' || sortBy === 'startAt') {
      aValue = aValue?.getTime?.() || 0;
      bValue = bValue?.getTime?.() || 0;
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const toggleRowExpansion = (bookingId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedRows(newExpanded);
  };

  const handleReleaseHold = async (booking) => {
    if (!window.confirm(`Release the hold for ${booking.customerName} on ${booking.date}? This frees the calendar slot.`)) {
      return;
    }
    try {
      setReleasingId(booking.id);
      await releaseHold(booking.id);
      await fetchBookings();
    } catch (error) {
      console.error('Error releasing hold:', error);
      window.alert(error.message || 'Failed to release hold');
    } finally {
      setReleasingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'hold':
        return { backgroundColor: '#1d4ed8', color: '#ffffff', border: '1px solid #3b82f6' };
      case 'confirmed':
      case 'booked':
        return { backgroundColor: GOLD, color: '#000000', border: `1px solid ${GOLD}` };
      case 'pending':
        return { backgroundColor: '#1f2937', color: '#ffffff', border: '1px solid #4b5563' };
      case 'cancelled':
      case 'canceled':
      case 'released':
      case 'expired':
        return { backgroundColor: '#7f1d1d', color: '#ffffff', border: '1px solid #ef4444' };
      default:
        return { backgroundColor: CARD_BG, color: '#ffffff', border: '1px solid #4b5563' };
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
        <div
          style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '999px',
            border: '2px solid #374151',
            borderTop: `2px solid ${GOLD}`,
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff', marginBottom: '0.5rem' }}>Bookings Management</h2>
                  <p style={{ color: '#d1d5db' }}>Manage all customer bookings and reservations</p>
                </div>
        <button
          onClick={fetchBookings}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: GOLD,
            color: '#000000',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.625rem 1rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <Calendar size={20} style={{ marginRight: '0.5rem' }} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(13.75rem, 100%), 1fr))', gap: '1rem' }}>
        <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ backgroundColor: 'rgba(201, 168, 76,0.12)', border: '1px solid rgba(201, 168, 76,0.35)', borderRadius: '0.5rem', padding: '0.55rem' }}>
            <Calendar style={{ color: GOLD }} size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>Total Bookings</h3>
            <p style={{ margin: 0, color: '#ffffff', fontWeight: 700, fontSize: '1.5rem' }}>{bookings.length}</p>
          </div>
        </div>
        
        <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ backgroundColor: 'rgba(201, 168, 76,0.12)', border: '1px solid rgba(201, 168, 76,0.35)', borderRadius: '0.5rem', padding: '0.55rem' }}>
            <Calendar style={{ color: GOLD }} size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>Confirmed</h3>
            <p style={{ margin: 0, color: '#ffffff', fontWeight: 700, fontSize: '1.5rem' }}>
              {bookings.filter(b => ['confirmed', 'booked'].includes((b.status || '').toLowerCase())).length}
            </p>
          </div>
        </div>
        
        <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ backgroundColor: 'rgba(37, 99, 235,0.18)', border: '1px solid rgba(59, 130, 246,0.45)', borderRadius: '0.5rem', padding: '0.55rem' }}>
            <Clock style={{ color: '#60a5fa' }} size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>Active Holds</h3>
            <p style={{ margin: 0, color: '#ffffff', fontWeight: 700, fontSize: '1.5rem' }}>
              {bookings.filter(b => (b.status || '').toLowerCase() === 'hold').length}
            </p>
          </div>
        </div>
        
        <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ backgroundColor: 'rgba(201, 168, 76,0.12)', border: '1px solid rgba(201, 168, 76,0.35)', borderRadius: '0.5rem', padding: '0.55rem' }}>
            <DollarSign style={{ color: GOLD }} size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, color: '#d1d5db', fontSize: '0.875rem' }}>Total Revenue</h3>
            <p style={{ margin: 0, color: '#ffffff', fontWeight: 700, fontSize: '1.5rem' }}>
              ${bookings.filter(b => (b.status || '').toLowerCase() !== 'hold').reduce((sum, b) => sum + (b.total || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', padding: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
                      <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} size={20} />
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#1a1a1a',
                  color: '#ffffff',
                  border: '1px solid #4b5563',
                  borderRadius: '0.5rem',
                  padding: '0.625rem 0.75rem 0.625rem 2.5rem',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.border = `1px solid ${GOLD}`;
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid #4b5563';
                }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: 'auto',
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
                border: '1px solid #4b5563',
                borderRadius: '0.5rem',
                padding: '0.6rem 0.75rem',
                outline: 'none',
              }}
            >
              <option value="all">All Status</option>
              <option value="hold">Hold</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="booked">Booked</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div style={{ backgroundColor: CARD_BG, border: '1px solid rgba(201, 168, 76,0.2)', borderRadius: '0.75rem', overflow: 'hidden' }}>
        {sortedBookings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#d1d5db' }}>
                  <Calendar style={{ margin: '0 auto', color: GOLD, marginBottom: '1rem' }} size={48} />
                  <h3 style={{ color: '#ffffff' }}>No Bookings Found</h3>
                  <p>No bookings match your current filters.</p>
                </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', color: '#ffffff' }}>
              <thead>
                <tr style={{ backgroundColor: '#0b0b0b' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Customer</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Date & Time</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Package</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Total</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: '#d1d5db', fontSize: '0.8rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBookings.map((booking) => (
                  <React.Fragment key={booking.id}>
                    <tr
                      style={{ borderTop: '1px solid #2a2a2a' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#161616';
                        e.currentTarget.style.boxShadow = 'inset 0 0 0 1px rgba(201, 168, 76,0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ width: '2.25rem', height: '2.25rem', backgroundColor: GOLD, borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <User style={{ color: '#000000' }} size={20} />
                          </div>
                          <div style={{ marginLeft: '0.75rem' }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#ffffff' }}>{booking.customerName}</div>
                            <div style={{ fontSize: '0.8rem', color: '#d1d5db' }}>{booking.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        <div style={{ fontSize: '0.9rem', color: '#ffffff' }}>
                          {formatStoredBookingDate(booking.date, { weekday: undefined })}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#d1d5db' }}>
                          {booking.time} ({booking.duration}h)
                        </div>
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        <div style={{ fontSize: '0.9rem', color: '#ffffff' }}>{booking.package}</div>
                        {booking.addOns.length > 0 && (
                          <div style={{ fontSize: '0.8rem', color: '#d1d5db' }}>
                            + {booking.addOns.join(', ')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        {(booking.status || '').toLowerCase() === 'hold' ? (
                          <>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#60a5fa' }}>Hold</div>
                            <div style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Not a booking</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#ffffff' }}>${booking.total}</div>
                            <div style={{ fontSize: '0.8rem', color: '#d1d5db' }}>Deposit: ${booking.deposit}</div>
                          </>
                        )}
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        <span style={{ ...getStatusColor(booking.status), display: 'inline-flex', padding: '0.2rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, borderRadius: '999px' }}>
                          {booking.status}
                        </span>
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            onClick={() => toggleRowExpansion(booking.id)}
                            style={{ padding: '0.25rem', color: '#d1d5db', background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            {expandedRows.has(booking.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          <button
                            onClick={() => onEditBooking(booking)}
                            style={{ padding: '0.25rem', color: GOLD, background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            <Edit size={16} />
                          </button>
                          {(booking.status || '').toLowerCase() === 'hold' && (
                            <button
                              onClick={() => handleReleaseHold(booking)}
                              disabled={releasingId === booking.id}
                              style={{
                                padding: '0.2rem 0.5rem',
                                color: '#ffffff',
                                background: '#1d4ed8',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: releasingId === booking.id ? 'not-allowed' : 'pointer',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                              }}
                            >
                              {releasingId === booking.id ? 'Releasing…' : 'Release'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Row */}
                    {expandedRows.has(booking.id) && (
                      <tr style={{ backgroundColor: '#0f0f0f' }}>
                        <td colSpan="6" style={{ padding: '1rem 0.75rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(13.75rem, 100%), 1fr))', gap: '1rem' }}>
                            <div>
                              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center' }}>
                                <Phone size={16} style={{ marginRight: '0.5rem' }} />
                                Contact Info
                              </h4>
                              <div style={{ fontSize: '0.82rem', color: '#d1d5db' }}>
                                <p>Phone: {booking.phone}</p>
                                <p>Email: {booking.email}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center' }}>
                                <MapPin size={16} style={{ marginRight: '0.5rem' }} />
                                Address
                              </h4>
                              <div style={{ fontSize: '0.82rem', color: '#d1d5db' }}>
                                <p>{booking.address}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center' }}>
                                <Calendar size={16} style={{ marginRight: '0.5rem' }} />
                                Booking Details
                              </h4>
                              <div style={{ fontSize: '0.82rem', color: '#d1d5db' }}>
                                <p>ID: {booking.bookingId}</p>
                                <p>Created: {booking.createdAt.toLocaleDateString()}</p>
                                {booking.startAt && (
                                  <p>Start: {booking.startAt.toLocaleString()}</p>
                                )}
                                {booking.endAt && (
                                  <p>End: {booking.endAt.toLocaleString()}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from {transform: rotate(0deg);} to {transform: rotate(360deg);} }`}</style>
    </div>
  );
};

export default BookingsTable;
